/**
 * Central catalog of RV Match AI purchasable products (consumer-side).
 *
 * Kept as a single source of truth so the homepage, checkout screens, and
 * marketing pages never drift from Stripe. Internal `id` values here MUST
 * match the product IDs configured in Stripe so the backend can look them
 * up when the checkout webhook fires.
 *
 * The Free RV Match quiz is intentionally NOT in this list — it stays free
 * forever and is never a Stripe product.
 *
 * Consumer upgrade ladder (marketing recommendation):
 *   Free Match → $59.99 Complete Toolkit → $149 VIP Plan → $399 Concierge
 */

// Public-CDN URLs for the product hero art. VIP Plan / Concierge point at
// stopgap gradient tiles until the artwork the founder is producing lands.
const IMG = {
  // Assets are stored newest-first; the user typed their images in reverse
  // order so the FIRST image shown in their message (Buyer Toolkit) is the
  // LAST asset uploaded (9omz0a75).
  cost_calculator: 'https://customer-assets-lqy194kg.emergentagent.net/job_7f460149-83c8-45b7-a740-ecf90deb88ce/artifacts/sgpqxb29_aa80db1f-163a-4bc3-a55a-1d5bfdbe0ad7.png',
  rv_comparison:   'https://customer-assets-lqy194kg.emergentagent.net/job_7f460149-83c8-45b7-a740-ecf90deb88ce/artifacts/qajwj5sl_a6eb7bb0-1ff4-4310-acd8-e33333cb32cb.png',
  deal_analyzer:   'https://customer-assets-lqy194kg.emergentagent.net/job_7f460149-83c8-45b7-a740-ecf90deb88ce/artifacts/lw0o95tc_66dbd0bc-79c6-4cb4-b156-7e1de1516c79.png',
  negotiation_coach: 'https://customer-assets-lqy194kg.emergentagent.net/job_7f460149-83c8-45b7-a740-ecf90deb88ce/artifacts/uqjsusmc_fea795af-da9b-4213-8854-1502d6465ca8.png',
  buyer_toolkit:   'https://customer-assets-lqy194kg.emergentagent.net/job_7f460149-83c8-45b7-a740-ecf90deb88ce/artifacts/9omz0a75_58992577-05b8-4a0c-bb41-321a2ff97c24.png',
  // VIP artwork — newest asset first (user's last-typed image = asset #1).
  // Order in their message: Onboarding, Founding, Buying Plan, Concierge.
  vip_buying_plan: 'https://customer-assets-lqy194kg.emergentagent.net/job_7f460149-83c8-45b7-a740-ecf90deb88ce/artifacts/puvkzlnu_d85bf803-d31a-4ce3-9e62-495f6cf8d28e.png',
  vip_concierge:   'https://customer-assets-lqy194kg.emergentagent.net/job_7f460149-83c8-45b7-a740-ecf90deb88ce/artifacts/2bkvexrx_573c6f1a-4139-4d02-9101-7cb877a2c4ac.png',
  // Advisor-facing artwork (not shown on customer homepage). Wired to the
  // advisor-signup / founding-advisor landing pages.
  dealer_founding: 'https://customer-assets-lqy194kg.emergentagent.net/job_7f460149-83c8-45b7-a740-ecf90deb88ce/artifacts/t4iach62_Screenshot%202026-08-13%20021157.png',
  dealer_setup:    'https://customer-assets-lqy194kg.emergentagent.net/job_7f460149-83c8-45b7-a740-ecf90deb88ce/artifacts/nn358k2e_Screenshot%202026-08-13%20021055.png',
  daily_matches:   'https://customer-assets-lqy194kg.emergentagent.net/job_7f460149-83c8-45b7-a740-ecf90deb88ce/artifacts/3pyhaecg_Screenshot%202026-08-13%20021458.png',
};

export const PRODUCTS = [
  {
    id: 'cost_calculator',
    stripe_price_id: 'price_1U3Ty4LOZ8CHROvmBcvzfKeL',
    name: 'RV Cost Calculator',
    price: 9.99,
    priceLabel: '$9.99',
    payment: 'one-time',
    tagline: 'Know what an RV really costs before you buy.',
    description:
      "Get a personalized estimate of the real costs of buying and owning an RV, including financing, insurance, maintenance, campground fees and other common expenses.",
    image: IMG.cost_calculator,
    highlight: false,
    accent: 'sky',
    href: '/checkout/cost_calculator',
    category: 'tool',
  },
  {
    id: 'rv_comparison',
    stripe_price_id: 'price_1U3TyuLOZ8CHROvmHQwofye3',
    name: 'RV vs. RV Comparison',
    price: 14.99,
    priceLabel: '$14.99',
    payment: 'one-time',
    tagline: 'The one you love vs. the one that fits you best.',
    description:
      "Compare two RVs side by side based on floorplan, features, sleeping capacity, tow compatibility, lifestyle fit, value and important compromises.",
    image: IMG.rv_comparison,
    highlight: false,
    accent: 'emerald',
    href: '/checkout/rv_comparison',
    category: 'tool',
  },
  {
    id: 'deal_analyzer',
    stripe_price_id: 'price_1U3TzpLOZ8CHROvmJ7T4QkGb',
    name: 'RV Deal Analyzer',
    price: 24.99,
    priceLabel: '$24.99',
    payment: 'one-time',
    tagline: "Is that sale offer actually a good deal? Get an independent read.",
    description:
      "Analyze any RV sale offer, including the selling price, fees, financing, trade details, potential concerns and overall deal quality.",
    image: IMG.deal_analyzer,
    highlight: false,
    accent: 'indigo',
    href: '/checkout/deal_analyzer',
    category: 'tool',
  },
  {
    id: 'negotiation_coach',
    stripe_price_id: 'price_1U3U0iLOZ8CHROvmV1doeGid',
    name: 'RV Negotiation Coach',
    price: 34.99,
    priceLabel: '$34.99',
    payment: 'one-time',
    tagline: 'Walk in with a script — save thousands.',
    description:
      "Receive a personalized RV negotiation strategy based on the vehicle, sale offer, trade, financing situation and your buying goals.",
    image: IMG.negotiation_coach,
    highlight: false,
    accent: 'rose',
    href: '/checkout/negotiation_coach',
    category: 'tool',
  },
  {
    id: 'daily_matches',
    // Dashboard-only product — hidden from the public marketing catalog
    // and cross-sell rows. Purchasable ONLY via the paywall banner
    // inside the buyer dashboard's Daily Matches tab, or as a bonus
    // bundled inside the Complete Buyer Toolkit. Kept in the PRODUCTS
    // array so PRODUCT_BY_ID lookups (checkout page + launcher) work.
    hiddenFromCatalog: true,
    // stripe_price_id is provisioned automatically on backend startup
    // (see routes/payment_routes.py::ensure_daily_matches_price). Left
    // blank here — the backend uses product_id → price_id via the
    // server-side CATALOG map, not this catalog file.
    name: 'RV Daily Matches',
    price: 19.99,
    priceLabel: '$19.99',
    payment: 'one-time',
    tagline: 'Three hand-picked RVs delivered to your dashboard every day. Forever.',
    description:
      "Fresh personalized RV matches every day based on your profile, budget, and tow rig. New listings, price drops, and units you may have missed — all delivered right to your dashboard. One-time payment, lifetime access.",
    image: IMG.daily_matches,
    highlight: false,
    accent: 'cyan',
    href: '/checkout/daily_matches',
    category: 'tool',
    features: [
      '3 fresh RV picks every day, forever',
      'Personalized to your quiz profile',
      'Filters by budget, tow rig, and RV type',
      'Direct link to view or contact seller',
      'One-time payment, lifetime feed',
    ],
  },
  {
    id: 'buyer_toolkit',
    stripe_price_id: 'price_1U3U1oLOZ8CHROvmTcQIlB8W',
    name: 'Complete RV Buyer Toolkit',
    price: 59.99,
    priceLabel: '$59.99',
    payment: 'one-time',
    tagline: 'All five tools. Save $34.96 vs buying separately.',
    description:
      "Get the complete RV Match AI buying system — RV Cost Calculator, RV vs. RV Comparison, Deal Analyzer, Negotiation Coach, AND Daily Matches (3 fresh AI-picked RVs delivered to your dashboard every day, for life).",
    image: IMG.buyer_toolkit,
    highlight: true,
    accent: 'amber',
    href: '/checkout/buyer_toolkit',
    category: 'bundle',
    includes: ['cost_calculator', 'rv_comparison', 'deal_analyzer', 'negotiation_coach', 'daily_matches'],
    savingsLabel: 'Save $34.96',
  },
  {
    id: 'vip_buying_plan',
    stripe_price_id: 'price_1U3U37LOZ8CHROvmPbKO9s8C',
    name: 'VIP RV Buying Plan',
    price: 149.00,
    priceLabel: '$149',
    payment: 'one-time',
    tagline: 'The toolkit + a personal review from RV Match AI.',
    description:
      "Get the Complete RV Buyer Toolkit plus personalized RV-buying guidance, including a review of your matches, tow validation, recommended shortlist, buying strategy and one consultation.",
    image: IMG.vip_buying_plan,
    highlight: false,
    accent: 'violet',
    href: '/checkout/vip_buying_plan',
    category: 'vip',
    features: [
      'Everything in the Complete Buyer Toolkit',
      'Personal review of your RV Match results',
      'Tow-vehicle validation',
      'Recommended shortlist',
      'Deal or pricing review',
      'Personalized buying plan',
      'One consultation with RV Match AI',
    ],
  },
  {
    id: 'vip_concierge',
    stripe_price_id: 'price_1U3U5kLOZ8CHROvm5E7A0hlN',
    name: 'VIP Buyer Concierge',
    price: 399.00,
    priceLabel: '$399',
    payment: 'one-time',
    tagline: 'Hands-on help until the deal is done. 30-day window.',
    description:
      "Receive hands-on RV-buying support for up to 30 days, including personalized matching, available-RV research, review of up to three sale offers, trade guidance, negotiation preparation and up to three consultations.",
    image: IMG.vip_concierge,
    highlight: false,
    accent: 'fuchsia',
    href: '/checkout/vip_concierge',
    category: 'vip',
    features: [
      'Everything in the VIP Buying Plan',
      'Deeper search for actual available RVs',
      'Review of up to 3 sale offers',
      'Trade-value guidance',
      'Negotiation preparation',
      'Up to 3 calls',
      '30-day support window',
    ],
  },
  // Dealer-facing products (NOT rendered on customer homepage — used on
  // dealer signup + billing pages).
  {
    id: 'dealer_founding_monthly',
    stripe_price_id: 'price_1U3U9ILOZ8CHROvm65n9r1TR',
    name: 'Founding Advisor Account',
    price: 2500.00,
    priceLabel: '$2,500/year',
    payment: 'recurring-yearly',
    tagline: 'Everything you need to run RV Match at your dealership.',
    description:
      "Yearly access to the RV Match AI advisor platform for one dealership location and up to five advisors, including an advisor-branded matching page, lead pipeline, inventory, appointments, sales tracking, marketing tools and analytics.",
    image: IMG.dealer_founding,
    highlight: false,
    accent: 'sky',
    href: '/checkout/dealer_founding_monthly',
    category: 'dealer',
  },
  {
    id: 'dealer_setup',
    stripe_price_id: 'price_1U3UA5LOZ8CHROvmn8SlY5Ed',
    name: 'Advisor Onboarding and Setup',
    price: 499.00,
    priceLabel: '$499',
    payment: 'one-time',
    tagline: 'White-glove setup and launch assistance.',
    description:
      "One-time advisor onboarding, including account configuration, profile and branding setup, inventory assistance, platform training and launch support.",
    image: IMG.dealer_setup,
    highlight: false,
    accent: 'indigo',
    href: '/checkout/dealer_setup',
    category: 'dealer',
  },
];

// Fast lookup by Stripe internal ID
export const PRODUCT_BY_ID = Object.fromEntries(PRODUCTS.map((p) => [p.id, p]));

// Convenient sub-groupings for section rendering
// `hiddenFromCatalog: true` products (e.g. daily_matches) are excluded from
// every public catalog surface — they're dashboard-only or bundle bonuses.
export const CONSUMER_TOOLS = PRODUCTS.filter((p) => p.category === 'tool' && !p.hiddenFromCatalog);
export const CONSUMER_BUNDLE = PRODUCTS.find((p) => p.category === 'bundle');
export const CONSUMER_VIP = PRODUCTS.filter((p) => p.category === 'vip' && !p.hiddenFromCatalog);
export const DEALER_PRODUCTS = PRODUCTS.filter((p) => p.category === 'dealer');
